import React from "react";
import {useTranslation} from "react-i18next";
import {
  ICartContextNoCart,
  isCart,
  useCartContext,
  useUnsafeCartContext,
} from "../CartContextProvider/CartContextProvider";
import * as Sentry from "@sentry/browser";
import * as ReactDOM from "react-dom";

interface IAddToCartButtonProps {
  itemId: number;
}

const buttonAttributes = {
  className: "button button-large product-cart__addbutton",
  style: {marginRight: "1rem"}
};

const AddToCartButtonPortal: React.FC<{targetNode: HTMLSpanElement | null; root: HTMLSpanElement | null}> = (props) => {
  const {t} = useTranslation();
  const context = useUnsafeCartContext();
  const {targetNode, root} = props;

  React.useEffect(() => {
    if (!root || !targetNode) {
      return;
    }

    root.style.display = "block";
    targetNode.replaceWith(root);

    // this will most likely not be necessary after we finally released the new cart for all
    const placeholder = root.parentElement?.querySelector<HTMLSpanElement>("span.button");
    if(placeholder) {
      placeholder.remove();
    }
  }, []);

  if (!root || !targetNode) {
    return null;
  }

  // if this is not there the item is most likely "nicht mehr lieferbar"
  const itemIdInput = targetNode.querySelector<HTMLInputElement>("[name=Item]");

  if(!itemIdInput) {
    return null;
  }

  // TODO this is not the best way to go, because there is a slight chance that the button will be clicked
  //   while the first request to check for a visitor cart is still not back. But for now this will do.
  if (!context) {
    return <button {...buttonAttributes}>{t("In den Warenkorb")}</button>;
  }

  const itemId = parseInt(itemIdInput.value, 10);

  return ReactDOM.createPortal(
    <React.Fragment>
      {isCart(context) ? (
        <AddToCartButton itemId={itemId} />
      ) : (
        <AddToCartButtonFallback context={context} itemId={itemId} />
      )}
    </React.Fragment>,
    root,
  );
};

const AddToCartButtonFallback: React.FC<{context: ICartContextNoCart; itemId: number}> = (props) => {
  const {t} = useTranslation("cart");
  const {requestCart, setItemsAdded} = props.context;
  const requestCartAndAddItem = () => {
    requestCart().then((result) => {
      const {cart, done} = result;
      cart.addItem(props.itemId, 1).then(() => {
        setItemsAdded([props.itemId]);
        done();
      }, Sentry.captureException);
    }, Sentry.captureException);
  };

  return (
    <button {...buttonAttributes} onClick={requestCartAndAddItem}>
      {t("In den Warenkorb")}
    </button>
  );
};

const AddToCartButton: React.FC<IAddToCartButtonProps> = (props) => {
  const context = useCartContext();
  const {cart} = context;
  const {t} = useTranslation("cart");

  const position = cart.positions.find(
    (pos) => pos.type === "Item" && !!pos.reference?.itemId && pos.item?.itemId === props.itemId,
  );
  const quantity = position?.quantity ?? 0;

  const addToCart = async () => {
    await cart.addItem(props.itemId, quantity + 1);
    context.setItemsAdded([props.itemId]);
  };

  return (
    <React.Fragment>
      <button {...buttonAttributes} onClick={addToCart}>
        {t("In den Warenkorb") + (quantity > 0 ? ` (${quantity})` : "")}
      </button>
    </React.Fragment>
  );
};

export default AddToCartButtonPortal;
